import { useMemo, useState } from 'react';

import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { Breakpoint, useMediaQuery, useTheme } from '@mui/material';
import { useClipboard, useDebouncedValue } from '@mantine/hooks';
import { useSnackbar } from 'notistack';

import { type RootState } from 'store';
import { endOfDay, formatISO, startOfDay } from 'date-fns';

import {
  resetOperationsFilter,
  useGetSelectedOperationsFilterOpts,
} from 'services/filters/operationsFilter/operationsFilter';
import {
  getSelectedTriggerIds,
  getSelectedTriggers,
  resetTriggerSelection,
} from 'services/filters/triggers/triggersFilterSlice';
import {
  getSelectedStatuses,
  getSelectedStatusIds,
} from 'services/filters/alertStatuses';
import {
  getSelectedFollowedBookmarked,
  resetFollowedBookmarkedSelection,
} from 'services/filters/alertFollowBookmark';
import {
  getSelectedAssignees,
  resetSelectedAssignees,
} from 'services/filters/alertAssignees';
import { getAlertMetadataFilters } from 'services/filters/alertMetadata';
import { resetStatusesSelection } from 'services/filters/alertStatuses/alertStatusesFilterSlice';
import {
  getReadUnread,
  resetReadUnreadSelection,
} from 'services/filters/alertReadUnread';
import { resetAlertMetadataFilters } from 'services/filters/alertMetadata/alertMetadataFilterSlice';
import useResetSelectedFilterIds from 'components/common/FilterMenu/useResetSelectedFilterIds';

const useOpen = () => {
  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return [open, handleOpen, handleClose] as const;
};

const useCustomMediaQuery = (breakpoint: Breakpoint) => {
  const theme = useTheme();

  return useMediaQuery(theme.breakpoints.down(breakpoint));
};

/**
 * @param {boolean} filterByMinute - Optional parameter to return the start and end dates based on the Date object's time instead of setting the time to the start and end of days respectively. Mainly used in combination with the DateTimeRange picker.
 */
const useDateRange = (filterByMinute?: boolean) => {
  const { selectedDateRange, timezone } = useSelector(
    (state: RootState) => state.dateRange2,
  );

  const startDate = (selectedDateRange && selectedDateRange[0]) || undefined;
  const endDate = (selectedDateRange && selectedDateRange[1]) || undefined;

  let isoStartDate: string | undefined;
  try {
    isoStartDate =
      startDate &&
      formatISO(filterByMinute ? startDate : startOfDay(startDate));
  } catch {
    isoStartDate = undefined;
  }

  let isoEndDate: string | undefined;
  try {
    isoEndDate =
      endDate && formatISO(filterByMinute ? endDate : endOfDay(endDate));
  } catch {
    isoEndDate = undefined;
  }

  const [debouncedIsoStartDate] = useDebouncedValue(isoStartDate, 200);
  const [debouncedIsoEndDate] = useDebouncedValue(isoEndDate, 200);

  const timezonedStartDate =
    debouncedIsoStartDate &&
    moment(debouncedIsoStartDate)
      .parseZone()
      .tz(timezone || Intl.DateTimeFormat().resolvedOptions().timeZone, true)
      .toISOString(true);

  const timezonedEndDate =
    debouncedIsoEndDate &&
    moment(debouncedIsoEndDate)
      .parseZone()
      .tz(timezone || Intl.DateTimeFormat().resolvedOptions().timeZone, true)
      .toISOString(true);

  return {
    startDate,
    endDate,
    isoStartDate: debouncedIsoStartDate,
    isoEndDate: debouncedIsoEndDate,
    timezonedStartDate,
    timezonedEndDate,
    timezone,
  };
};

const useAllFilters = () => {
  const {
    startDate,
    endDate,
    isoStartDate,
    isoEndDate,
    timezonedStartDate,
    timezonedEndDate,
  } = useDateRange();

  const triggerIds = useSelector(getSelectedTriggerIds);
  const triggers = useSelector(getSelectedTriggers);
  const statusIds = useSelector(getSelectedStatusIds);
  const statuses = useSelector(getSelectedStatuses);
  const selectedOperationsFilterOpts = useGetSelectedOperationsFilterOpts();
  const { bookmarked, followed } = useSelector(getSelectedFollowedBookmarked);
  const selectedAssignees = useSelector(getSelectedAssignees);
  const timezone = useSelector((state: RootState) => state.dateRange2.timezone);
  const { readUnread } = useSelector(getReadUnread);

  const selectedFollowedBookmarkedOpts = useMemo(
    () => bookmarked.concat(followed),
    [bookmarked, followed],
  );

  const alertMetadataFilters = useSelector(getAlertMetadataFilters);

  return useMemo(
    () => ({
      startDate,
      endDate,
      triggerIds,
      triggers,
      statusIds,
      statuses,
      isoStartDate,
      isoEndDate: isoEndDate as string | undefined,
      selectedOperationsFilterOpts,
      selectedFollowedBookmarkedOpts,
      assignees: selectedAssignees,
      timezone,
      timezonedStartDate,
      timezonedEndDate,
      alertMetadataFilters,
      readUnread,
    }),
    [
      startDate,
      endDate,
      triggerIds,
      statusIds,
      statuses,
      isoStartDate,
      isoEndDate,
      selectedOperationsFilterOpts,
      selectedAssignees,
      timezone,
      timezonedStartDate,
      timezonedEndDate,
      selectedFollowedBookmarkedOpts,
      alertMetadataFilters,
      readUnread,
    ],
  );
};

const useResetAllFiltersExceptDate = () => {
  const dispatch = useDispatch();
  const resetSelectedFilterIds = useResetSelectedFilterIds();

  const resetAllFiltersExceptDate = () => {
    dispatch(resetTriggerSelection());
    dispatch(resetStatusesSelection());
    dispatch(resetOperationsFilter());
    dispatch(resetFollowedBookmarkedSelection());
    dispatch(resetSelectedAssignees());
    dispatch(resetReadUnreadSelection());
    dispatch(resetAlertMetadataFilters());

    resetSelectedFilterIds();
  };

  return resetAllFiltersExceptDate;
};

const useCopyWithNotification = (timeout?: number) => {
  const { copy, copied, error, reset } = useClipboard({ timeout });
  const { enqueueSnackbar } = useSnackbar();

  const copyAndNotify = (text: string) => {
    copy(text);

    if (error) {
      enqueueSnackbar('Error copying to clipboard', { variant: 'error' });
    } else {
      enqueueSnackbar('Copied to clipboard', { variant: 'success' });
    }
  };

  return { copyAndNotify, copied, error, reset };
};

export {
  useOpen,
  useDateRange,
  useAllFilters,
  useCustomMediaQuery,
  useCopyWithNotification,
  useResetAllFiltersExceptDate,
};
